
import { useState, useCallback, useRef } from 'react';
import { WeeklyMenu } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useThrottle } from '@/hooks/useDebounce';
import { createNewMenu, getCurrentWeek } from './menuUtils';

export const useMenuSync = (userId: string | undefined, generateShoppingList: () => Promise<any>) => {
  const [weeklyMenu, setWeeklyMenu] = useState<WeeklyMenu | null>(null);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);
  
  const week = getCurrentWeek();
  
  const loadMenu = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Try loading from Supabase first
      const { data: menuData, error } = await supabase
        .from('weekly_menus')
        .select('*')
        .eq('user_id', userId)
        .eq('week', week)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading menu from Supabase:', error);
      }

      if (menuData) {
        const transformedMenu = {
          ...menuData,
          userId: menuData.user_id,
          nutritionGoals: menuData.nutrition_goals || {}
        };
        
        setWeeklyMenu(transformedMenu);
      } else {
        // Create new menu if not found
        const newMenu = createNewMenu(userId);
        setWeeklyMenu(newMenu);
        await saveMenuToSupabase(newMenu);
      }
      
    } catch (error) {
      console.error('Error loading menu:', error);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [userId, week]);
  
  // Throttled save function
  const saveMenuToSupabase = useThrottle(async (menu: WeeklyMenu) => {
    if (!menu || !menu.userId) return;
    
    try {
      const supabaseFormattedMenu = {
        id: `${menu.userId}_${menu.week}`,
        user_id: menu.userId,
        days: menu.days,
        week: menu.week,
        updated_at: new Date().toISOString(),
        created_at: menu.created_at || new Date().toISOString(),
        nutrition_goals: menu.nutritionGoals || {}
      };
      
      const { error } = await supabase
        .from('weekly_menus')
        .upsert(supabaseFormattedMenu, { 
          onConflict: 'id',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error saving menu to Supabase:', error);
      }
    } catch (error) {
      console.error('Error saving menu to Supabase:', error);
    }
  }, 2000);
  
  const updateMenu = useCallback(async (updatedMenu: WeeklyMenu) => {
    if (!updatedMenu || !updatedMenu.userId) return;
    
    // Update state immediately
    setWeeklyMenu(updatedMenu);
    
    // Save to Supabase (throttled)
    await saveMenuToSupabase(updatedMenu);
  }, [saveMenuToSupabase]);
  
  const refreshMenu = useCallback(async (): Promise<void> => {
    await loadMenu();
  }, [loadMenu]);
  
  return {
    weeklyMenu,
    loading,
    setWeeklyMenu,
    loadMenu,
    updateMenu,
    refreshMenu
  };
};
